/* eslint-disable @atlassian/tangerine/import/no-parent-imports */
import { getActiveProducts } from '../active-products';
import { planProvisioning } from '../provisioning-api';

import type {
	ActivationCapability,
	ModalData,
	PersonalizationResponse,
	Pricing,
	TapTrait,
} from './types';

const isNullOrUndefined = <T>(value: T | null | undefined): value is null | undefined =>
	value === null || value === undefined;

const getActivationCapabilityFromReasons = (reasons?: string[]): ActivationCapability => {
	if (reasons?.some((reason) => reason === 'user-cannot-add-product')) {
		return 'requires-approval';
	}
	return 'can-activate';
};

const fetchSiteTraits = async (cloudId: string): Promise<PersonalizationResponse> => {
	const response = await fetch(
		`/gateway/api/tap-delivery/api/v3/personalization/site/${encodeURIComponent(cloudId)}`,
		{
			method: 'GET',
			credentials: 'include',
			headers: {
				'Content-Type': 'application/json',
				'x-atlassian-mau-ignore': 'true',
			},
		},
	);

	if (!response.ok) {
		throw new Error(
			`Non-OK response when fetching site traits: HTTP ${response.status}: ${response.statusText}`,
		);
	}

	return response.json();
};

const getPriceIncreaseTraitValue = async (cloudId: string): Promise<Pricing> => {
	const response = await fetchSiteTraits(cloudId);

	const hasTrait = response.attributes.find(
		(trait: TapTrait) => trait.name === 'site-twc-price-percentage-increase',
	);
	if (!hasTrait) {
		return { segmentation: 'increase' };
	}

	const priceChangeValue = hasTrait.value as number;
	if (!Number.isInteger(priceChangeValue)) {
		return { segmentation: 'increase' };
	}

	if (priceChangeValue < 0) {
		return { segmentation: 'decrease', value: priceChangeValue };
	} else if (priceChangeValue === 0) {
		return { segmentation: 'same', value: priceChangeValue };
	}

	return { segmentation: 'increase', value: priceChangeValue };
};

export const fetchModalData = async (
	cloudId: string,
	sourceComponent: string,
	sourceContext: string,
): Promise<ModalData> => {
	const [userProducts, planResponse, pricing] = await Promise.all([
		getActiveProducts(cloudId),
		planProvisioning({ cloudId, sourceComponent, sourceContext }),
		getPriceIncreaseTraitValue(cloudId),
	]);

	const allowed = planResponse.allowed;
	const inProgress =
		planResponse?.reasons?.some((reason: string) => reason === 'Job is already running') ?? false;

	const provisioningCompleted =
		planResponse?.reasons?.some((reason: string) => reason === 'product-already-active') ?? false;
	const status = provisioningCompleted ? 'PROVISIONING_COMPLETED' : planResponse?.context?.status; // future behavior: xflow will only return status if the job is running

	const hasPaymentDetails = planResponse?.tasks?.[0]?.context?.hasPaymentDetails;
	if (isNullOrUndefined(hasPaymentDetails)) {
		throw new Error('Missing hasPaymentDetails from plan endpoint');
	}

	const edition = planResponse?.tasks?.[0]?.context?.edition?.id;
	if (isNullOrUndefined(edition)) {
		throw new Error('Missing edition from plan endpoint');
	}

	const activationCapability = getActivationCapabilityFromReasons(planResponse?.reasons);

	return {
		modalData: {
			edition,
			hasPaymentDetails,
			userProducts,
			status,
			priceSegmentation: pricing.segmentation,
			priceValue: pricing.value,
			activationCapability,
		},
		allowed,
		inProgress,
	};
};
